import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

const useDimensions = (defaultRect = {}, types = []) => {
  const [node, setNode] = useState(null);
  const [rect, setRect] = useState(defaultRect);
  const [animationFrameID, setAnimationFrameID] = useState(null);
  const [resizeObserver, setResizeObserver] = useState(null);
  const ref = useCallback((newNode) => {
    if (newNode !== null) {
      setNode(newNode);
      setRect({ entry: newNode.getBoundingClientRect() });
    }
  }, []);
  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver((entries) =>
      measure(entries, node, setAnimationFrameID, setRect, types)
    );
    setResizeObserver(resizeObserver);
    return () => {
      window.cancelAnimationFrame(animationFrameID);
      resizeObserver.disconnect();
    };
  }, [node]);
  useEffect(() => {
    if (node && resizeObserver) {
      resizeObserver.observe(node);
    }
  }, [node, resizeObserver]);
  return [rect, ref];
};

const measure = (entries, node, setAnimationFrameID, setRect, types) => {
  const contentRect = getContentRect(node, types);
  if (entries) contentRect.entry = entries[0].contentRect;
  setAnimationFrameID(
    window.requestAnimationFrame(() => {
      setRect(contentRect);
    })
  );
};

const getContentRect = (node, types = []) => {
  const calculations = {};
  if (types.isArray) {
    if (types.includes('client')) {
      calculations.client = {
        top: node.clientTop,
        left: node.clientLeft,
        width: node.clientWidth,
        height: node.clientHeight,
      };
    }
    if (types.includes('offset')) {
      calculations.offset = {
        top: node.offsetTop,
        left: node.offsetLeft,
        width: node.offsetWidth,
        height: node.offsetHeight,
      };
    }
    if (types.includes('scroll')) {
      calculations.scroll = {
        top: node.scrollTop,
        left: node.scrollLeft,
        width: node.scrollWidth,
        height: node.scrollHeight,
      };
    }
    if (types.includes('bounds') && node) {
      const rect = node.getBoundingClientRect();
      calculations.bounds = {
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      };
    }
    if (types.includes('margin')) {
      const styles = getComputedStyle(node);
      calculations.margin = {
        top: styles ? parseInt(styles.marginTop) : 0,
        right: styles ? parseInt(styles.marginRight) : 0,
        bottom: styles ? parseInt(styles.marginBottom) : 0,
        left: styles ? parseInt(styles.marginLeft) : 0,
      };
    }
  }
  return calculations;
};

export default useDimensions;
